import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/constants";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const TT_STYLE = { borderRadius: 12, border: "1px solid #1B3A5C20" };

// "2026-07" → "Jul 2026", "2026-07-11" → "11 Jul" for compact trend-axis ticks.
const periodLabel = (period, granularity) => {
  if (!period) return period;
  if (granularity === "month") {
    const d = new Date(`${period}-01T00:00:00`);
    return Number.isNaN(d.getTime()) ? period
      : d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  }
  const d = new Date(`${period}T00:00:00`);
  return Number.isNaN(d.getTime()) ? period
    : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// Embedded in the Reports page as the "Revenue" sub-tab. The period comes from
// the page-level shared filter via the `from`/`to` props.
// branchId is the Reports page's shared branch filter ("" / undefined = all branches).
export default function RevenueReport({ from, to, branchId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = ["super_admin", "hq_admin"].includes(user?.role);

  const load = useCallback(async (f, t, b) => {
    setLoading(true);
    setError(null);
    try {
      const params = { from: f, to: t };
      if (b) params.branch_id = b;
      const r = await api.get("/reports/revenue", { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.status === 403
        ? "You don't have access to the revenue report."
        : "Could not load the revenue report.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load(from, to, branchId);
  }, [isAdmin, from, to, branchId, load]);

  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-navy/10 bg-white p-8 text-center text-sm text-muted-foreground shadow-sm">
        The revenue report is available to Super Admin and HQ Admin only.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error ? (
        <Card title="Revenue Report">
          <p className="py-8 text-center text-sm text-muted-foreground">{error}</p>
        </Card>
      ) : loading || !data ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-white/60" />)}
        </div>
      ) : (
        <RevenueBody data={data} />
      )}
    </div>
  );
}

function RevenueBody({ data }) {
  const k = data.kpis || {};
  const granularity = data.granularity || "day";

  const trend = useMemo(
    () => (data.trend || []).map((t) => ({ ...t, name: periodLabel(t.period, granularity) })),
    [data.trend, granularity]
  );

  return (
    <div className="space-y-5">
      {/* KPI tiles */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Collected" value={inr(k.collected)} />
        <KpiCard label="Outstanding" value={inr(k.outstanding)} />
        <KpiCard label="Billed" value={inr(k.billed)} />
        <KpiCard
          label="Visits"
          value={Number(k.visits || 0).toLocaleString("en-IN")}
          sub={`${Number(k.converted || 0).toLocaleString("en-IN")} converted`}
        />
        <KpiCard label="Avg per Visit" value={inr(k.avg_per_visit)} />
      </div>

      {/* Trend chart */}
      <Card title="Collected vs Outstanding" subtitle={granularity === "month" ? "Per month" : "Per day"}>
        {trend.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No revenue in this period</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={trend} margin={{ left: 4, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} interval="preserveStartEnd" minTickGap={12} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} width={70}
                tickFormatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
              <Tooltip contentStyle={TT_STYLE} formatter={(v) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="collected" name="Collected" fill="#1B3A5C" radius={[6, 6, 0, 0]} />
              <Line dataKey="outstanding" name="Outstanding" stroke="#C49A6C" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Breakdown tables — 2-column grid */}
      <div className="grid gap-5 lg:grid-cols-2">
        <BreakdownCard title="By Centre" rows={data.by_centre} nameKey="name" />
        <BreakdownCard title="By Doctor" rows={data.by_doctor} nameKey="name" />
        <BreakdownCard title="By Service" rows={data.by_service} nameKey="name" />
        <BreakdownCard title="By Source" rows={data.by_source} nameKey="name" />
      </div>

      {/* By Outcome strip */}
      <OutcomeStrip rows={data.by_outcome} />

      {/* Target vs Achieved */}
      <TargetVsAchieved rows={data.target_vs_achieved} />

      {/* Outstanding ledger */}
      <OutstandingLedger rows={data.outstanding_ledger} />
    </div>
  );
}

/* ── Breakdown table (name / collected / outstanding / appt booked / visits) ──
 * "Appt. Booked" sits next to Visits on purpose: the two read as a funnel
 * (booked → showed up). They deliberately don't tie out — Booked counts bookings
 * MADE in the window (created_at, any status) while Visits counts visits that
 * HAPPENED in it, so a booking made this month for next month lands in one only.
 */
function BreakdownCard({ title, rows, nameKey }) {
  const list = rows || [];
  const num = (n) => Number(n || 0).toLocaleString("en-IN");
  return (
    <Card title={title}>
      <div className="overflow-x-auto">
        <table className="w-full whitespace-nowrap text-sm">
          <thead>
            <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3 text-right">Collected</th>
              <th className="px-4 py-3 text-right">Outstanding</th>
              <th className="px-4 py-3 text-right">Appt. Booked</th>
              <th className="px-4 py-3 text-right">Visits</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No data</td></tr>
            ) : (
              list.map((r, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-nude/30">
                  <td className="px-4 py-3 font-semibold text-ink">{r[nameKey] || "—"}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{inr(r.collected)}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{inr(r.outstanding)}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{num(r.booked)}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{num(r.visits)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ── By Outcome: converted vs not-converted revenue strip ─────────────────── */
const OUTCOME_LABELS = { converted: "Converted", not_converted: "Not Converted" };
function OutcomeStrip({ rows }) {
  const list = rows || [];
  return (
    <Card title="By Outcome">
      {list.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No visits in this period</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {list.map((o) => (
            <div key={o.outcome} className="rounded-xl border border-navy/10 bg-cream/40 p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {OUTCOME_LABELS[o.outcome] || o.outcome}
              </p>
              <p className="mt-1 font-display text-2xl font-semibold text-navy">{inr(o.collected)}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {Number(o.visits || 0).toLocaleString("en-IN")} visit{o.visits === 1 ? "" : "s"}
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ── Target vs Achieved: centres as rows, months as column groups ─────────── */
const thGroup = "border px-3 py-2 text-center text-xs font-semibold text-navy bg-cream/60";
const thSub = "border px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-cream/40";
const td = "border px-3 py-2 text-center text-sm text-ink";

function TargetVsAchieved({ rows }) {
  const list = rows || [];
  // Every centre carries the same month window; derive the header from the first.
  const months = list[0]?.months || [];

  return (
    <Card title="Target vs Achieved">
      {list.length === 0 || months.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No targets set for this period</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thGroup} rowSpan={2}>Centre</th>
                {months.map((m, i) => <th key={i} className={thGroup} colSpan={3}>{m.label}</th>)}
              </tr>
              <tr>
                {months.map((m, i) => (
                  <React.Fragment key={i}>
                    <th className={thSub}>Target</th><th className={thSub}>Achieved</th><th className={thSub}>% Ach.</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.branch_id || row.centre}>
                  <td className={`${td} font-semibold`}>{row.centre}</td>
                  {row.months.map((mm, i) => (
                    <React.Fragment key={i}>
                      <td className={`${td} text-right`}>{mm.target ? inr(mm.target) : "—"}</td>
                      <td className={`${td} text-right`}>{inr(mm.achieved)}</td>
                      <td className={`${td} font-semibold ${mm.pct >= 100 ? "bg-green-100 text-green-800" : mm.pct < 60 ? "bg-red-100 text-red-700" : ""}`}>{mm.pct}%</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ── Outstanding ledger ───────────────────────────────────────────────────── */
function OutstandingLedger({ rows }) {
  const list = rows || [];
  return (
    <Card title="Outstanding Ledger">
      {list.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">No outstanding amounts 🎉</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Patient</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">Doctor</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Revenue</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.appointment_id} className="border-b last:border-0 hover:bg-nude/30">
                  <td className="px-4 py-3 font-semibold text-ink">{r.patient_name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{maskPhone(r.mobile) || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.branch || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.doctor || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(r.scheduled_at)}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{inr(r.revenue)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-700">{inr(r.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ── Shared shells (mirrors Reports.jsx) ──────────────────────────────────── */
const KpiCard = ({ label, value, sub }) => (
  <div className="rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-2xl font-semibold text-navy">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const Card = ({ title, subtitle, children }) => (
  <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
    <h3 className="font-display text-lg font-semibold text-navy">{title}</h3>
    {subtitle && <p className="mb-4 text-sm text-muted-foreground">{subtitle}</p>}
    {!subtitle && <div className="mb-4" />}
    {children}
  </div>
);
